import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useState, type ReactNode } from 'react';
import { Avatar, Badge, Box, Dialog, IconButton, type AvatarProps } from '@/components/common';
import { cn } from '@/lib/utils';
import type { ProfilePhoto } from '@/types';

/**
 * Round photo thumbnail; tapping it opens an enlarged preview modal. With
 * `count`, overlays a like badge. `disabled` renders it inert (blurred/locked
 * contexts) and suppresses both the badge and the preview.
 */
export function PhotoThumbnail({
  photo,
  count = 0,
  size = '4',
  disabled,
  fallback = '?',
  alt,
}: {
  photo: ProfilePhoto;
  count?: number;
  size?: AvatarProps['size'];
  disabled?: boolean;
  fallback?: NonNullable<ReactNode>;
  alt?: string;
}) {
  const { t } = useTranslation('chat');
  const { t: tCommon } = useTranslation('common');
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        aria-label={alt ?? t('likes.photoAlt', { index: (photo.position ?? 0) + 1 })}
        className={cn('relative', !disabled && 'cursor-pointer')}
      >
        <Avatar src={photo.url} radius="full" size={size} fallback={fallback} />
        {!disabled && count > 0 && (
          <Badge
            color="red"
            size="1"
            className="absolute -right-1 -bottom-1"
            data-testid={`like-count-${photo.id}`}
          >
            {count}
          </Badge>
        )}
      </button>

      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Content size="2" aria-describedby={undefined} className="relative">
          <Dialog.Title className="sr-only">{t('likes.previewTitle')}</Dialog.Title>
          <Box position="absolute" top="3" right="3" className="z-10">
            <IconButton
              type="button"
              variant="soft"
              color="gray"
              highContrast
              size="2"
              aria-label={tCommon('actions.close')}
              onClick={() => setOpen(false)}
            >
              <X size={20} aria-hidden />
            </IconButton>
          </Box>
          <Box mx="auto" overflow="hidden" style={{ borderRadius: 'var(--radius-6)' }}>
            <img
              src={photo.url}
              alt={t('likes.previewTitle')}
              style={{
                display: 'block',
                margin: '0 auto',
                maxWidth: '100%',
                maxHeight: '60vh',
              }}
            />
          </Box>
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
}
